import type { VisitRow } from '@busy/api';

// "Most requested" computation for a client's visit history.
//
// Why client-side: useClientVisits already returns the whole row set for
// a single client, and the picker / detail modal already mount it. Doing
// the reduce here keeps the picker free of an extra round-trip and lives
// next to the UI that consumes it.
//
// Matching policy — visit.service_id (FK) is authoritative when present;
// fall back to lower(trim(cut_type)) so historical / imported / walk-in
// rows without a service_id still aggregate against the matching catalog
// entry. Same fallback the EOD/recap surfaces use.
//
// Tie-breaker — when two services land at the same count, the more
// RECENT one wins. Reasoning: a client who used to get fades and now
// gets buzzes should see "Buzz" as their current favorite, not whichever
// got recorded first.
//
// The visit count threshold for surfacing a "Most Requested" badge is
// 2. One visit is not a pattern — at that point ANY service the client
// has had once would be tied at 1 and the crown becomes noise. Two+
// hits one item = signal.

export type TopPick = {
  /** Matches visit.service_id when set. Null for built-in CUT_TYPES
   *  that don't have a service row (the catalog row's id is a slug
   *  like 'classic-haircut', stored in cut_type instead). */
  serviceId: string | null;
  /** Normalized name — lower-cased + trimmed — for the catalog-side
   *  fallback match when serviceId is null. The display side renders
   *  whatever the catalog's localized name is. */
  normalizedName: string;
  /** Number of times this service appeared in the client's history. */
  count: number;
  /** ISO timestamp of the most recent visit with this service. Used
   *  for tie-breaking + future "last had this on X" displays. */
  lastVisitAt: string;
};

export type ClientTopPicks = {
  service: TopPick | null;
  addon: TopPick | null;
};

/** Minimum visit count required to surface a crown. See module header. */
export const MOST_REQUESTED_MIN_COUNT = 2;

export function aggregateClientTopPicks(visits: VisitRow[]): ClientTopPicks {
  const serviceCounts = new Map<string, TopPick>();
  const addonCounts = new Map<string, TopPick>();

  for (const v of visits) {
    // Service key — prefer service_id, fall back to normalized cut_type.
    // A row missing both is skipped (would never aggregate against the
    // catalog anyway).
    const sName = (v.cut_type ?? '').trim();
    const sNormalized = sName.toLowerCase();
    const sKey = v.service_id ?? (sNormalized ? `name:${sNormalized}` : null);
    if (sKey) {
      const existing = serviceCounts.get(sKey);
      if (existing) {
        existing.count += 1;
        if (v.started_at > existing.lastVisitAt) existing.lastVisitAt = v.started_at;
      } else {
        serviceCounts.set(sKey, {
          serviceId: v.service_id,
          normalizedName: sNormalized,
          count: 1,
          lastVisitAt: v.started_at,
        });
      }
    }

    // Add-ons — walk the JSONB array. visit.addons is shaped
    // { id, name, priceCents } per VisitAddonRow.
    for (const a of v.addons ?? []) {
      const aNormalized = (a.name ?? '').trim().toLowerCase();
      const aKey = a.id ?? (aNormalized ? `name:${aNormalized}` : null);
      if (!aKey) continue;
      const existing = addonCounts.get(aKey);
      if (existing) {
        existing.count += 1;
        if (v.started_at > existing.lastVisitAt) existing.lastVisitAt = v.started_at;
      } else {
        addonCounts.set(aKey, {
          serviceId: a.id ?? null,
          normalizedName: aNormalized,
          count: 1,
          lastVisitAt: v.started_at,
        });
      }
    }
  }

  // Pick winners: highest count, recency breaks ties.
  function topOf(map: Map<string, TopPick>): TopPick | null {
    let top: TopPick | null = null;
    for (const pick of map.values()) {
      if (!top) {
        top = pick;
        continue;
      }
      if (pick.count > top.count) top = pick;
      else if (pick.count === top.count && pick.lastVisitAt > top.lastVisitAt) top = pick;
    }
    if (!top || top.count < MOST_REQUESTED_MIN_COUNT) return null;
    return top;
  }

  return { service: topOf(serviceCounts), addon: topOf(addonCounts) };
}

/** Does this catalog item match the given top pick? Compares serviceId
 *  first; falls back to normalized name. */
export function matchesTopPick(
  pick: TopPick | null,
  candidate: { id?: string | null; name?: string | null; nameKey?: string | null; resolvedName?: string },
): boolean {
  if (!pick) return false;
  if (pick.serviceId && candidate.id && pick.serviceId === candidate.id) return true;
  const candidateName = (candidate.resolvedName ?? candidate.name ?? '').trim().toLowerCase();
  if (candidateName && candidateName === pick.normalizedName) return true;
  return false;
}
